import {
  Box,
  BoxProps,
  Button,
  Heading,
  HeadingProps,
  HStack,
  IconButton,
  useDisclosure,
} from '@cardboard-ui/react';
import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { t, Trans } from '@lingui/macro';
import { lazy } from '@loadable/component';
import { Card, CardContent } from 'components/cards/Card';
import { createIcon } from 'components/icons/createIcon';
import { SuspendWithSpinner } from 'components/SuspendWithSpinner';
import React from 'react';

export const PrivateIcon = createIcon(light('eye'));
export const PrivateHiddenIcon = createIcon(light('eye-slash'));
const PrivateNotesEditor = lazy(() => import('../PrivateNotesEditor'));

type PrivateNoteProps = {
  nodeId: string;
  headingProps?: HeadingProps;
  boxProps?: BoxProps;
};

export const PrivateNote = ({
  nodeId,
  headingProps = { size: 'sm', fontWeight: 'normal' },
  boxProps = {
    bg: 'white',
    _dark: { bg: 'gray.700' },
    borderRadius: 'md',
    p: 1,
    minHeight: 4 * 6,
  },
}: PrivateNoteProps) => {
  const { isOpen: isHidden, onToggle: onToggleHidden } = useDisclosure({
    defaultIsOpen: true,
  });

  return (
    <Box pos="relative">
      <HStack spacing={1}>
        <IconButton
          onClick={onToggleHidden}
          size="sm"
          icon={isHidden ? <PrivateHiddenIcon /> : <PrivateIcon />}
          variant="ghost"
          aria-label={isHidden ? t`Show note` : t`Hide note`}
        />
        <Heading {...headingProps}>{t`Private note`}</Heading>
      </HStack>

      <Card isNotClickable>
        <CardContent>
          {isHidden ? (
            <Box
              p={2}
              onClick={onToggleHidden}
              minHeight={boxProps.minHeight || boxProps.minH || 4 * 6}
              cursor="pointer"
            >
              <Trans>
                <Box>
                  For your privacy, this note is hidden.{' '}
                  <Button variant="link">Click here</Button> to show the private
                  note.
                </Box>
              </Trans>
            </Box>
          ) : (
            <SuspendWithSpinner>
              <PrivateNotesEditor
                nodeId={nodeId}
                placeholder={t`Click to add a private note.`}
              />
            </SuspendWithSpinner>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PrivateNote;
